import { take } from 'rxjs/operators';
import { wrapIntoObservable } from '../utils';
import { lastValueFrom } from 'rxjs';
/**
 * @param {Injector} injector
 * @param {ActivatedRoute} activatedRoute
 * @param {Router} router
 * @returns resolved data
 */
export function dataResolverFactory(injector, activatedRoute, router) {
    /**
     * @param {JssResolve<unknown> | Type<JssResolve<unknown>>} resolver
     * @returns resolver instance
     */
    function getResolverInstance(resolver) {
        return 'resolve' in resolver ? resolver : injector.get(resolver);
    }
    /**
     * @param {ComponentFactoryResult} factory
     * @returns {Array<[string, JssResolve<unknown>]>} resolver instances
     */
    function collectResolverInstances(factory) {
        if (factory.resolve) {
            const resolve = factory.resolve;
            return Object.keys(factory.resolve).map((key) => [
                key,
                getResolverInstance(resolve[key]),
            ]);
        }
        return [];
    }
    /**
     * @param {JssResolve<unknown>} resolver
     * @param {ComponentFactoryResult} factory
     * @returns data
     */
    function _resolveData(resolver, factory) {
        const data = resolver.resolve({
            activatedRoute: activatedRoute.snapshot,
            routerState: router.routerState.snapshot,
            rendering: factory.componentDefinition,
        });
        const data$ = wrapIntoObservable(data);
        return lastValueFrom(data$.pipe(take(1)));
    }
    return function resolveData(factories) {
        return Promise.all(factories.map((factory) => {
            const resolvers = collectResolverInstances(factory);
            const pendingData = resolvers.map(([key, resolver]) => _resolveData(resolver, factory).then((data) => [key, data]));
            return Promise.all(pendingData)
                .then((allData) => allData.reduce((acc, [key, data]) => {
                acc[key] = data;
                return acc;
            }, {}))
                .then((data) => ({ factory, data }));
        }));
    };
}
